import {
  Chip,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Datagrid,
  DateField,
  DateInput,
  Edit,
  Filter,
  FunctionField,
  List,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  useRecordContext,
} from "react-admin";
import { format, parseISO } from "date-fns";

import BookingRequestActions from "../components/BookingRequestActions";
import {
  BookingRequestRecord,
  getEffectiveStatus,
} from "../services/bookingRequests";

const VIEW_CHOICES = [
  { id: "open", name: "Open" },
  { id: "expired", name: "Expired" },
  { id: "approved", name: "Approved" },
  { id: "rejected", name: "Rejected" },
  { id: "all", name: "All" },
];

const REQUEST_TYPE_CHOICES = [
  "Appointment Visit",
  "Private Party",
  "Listening Party",
  "Event or Showcase",
  "Meeting or Workshop",
  "Photo or Video Shoot",
  "Brand Activation",
  "Other",
].map((type) => ({ id: type, name: type }));

const STATUS_CHIP_COLORS: Record<
  string,
  "info" | "warning" | "success" | "error" | "default"
> = {
  new: "info",
  contacted: "warning",
  approved: "success",
  rejected: "error",
  expired: "default",
};

const formatDate = (date?: string) => {
  if (!date) return "";
  try {
    return format(parseISO(date), "EEE, MMM d, yyyy");
  } catch {
    return date;
  }
};

const formatTime = (time?: string) => {
  if (!time) return "";
  try {
    return format(parseISO(`1970-01-01T${time}`), "h:mm a");
  } catch {
    return time;
  }
};

const StatusChip = () => {
  const record = useRecordContext<BookingRequestRecord>();
  if (!record) return null;
  const status = getEffectiveStatus(record);
  return (
    <Chip
      size="small"
      label={status}
      color={STATUS_CHIP_COLORS[status] || "default"}
    />
  );
};

const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

const BookingRequestFilter = (props: any) => (
  <Filter {...props}>
    <SelectInput label="View" source="view" choices={VIEW_CHOICES} alwaysOn />
    <TextInput label="Search Name" source="name" alwaysOn />
    <TextInput label="Search Email" source="email" />
    <SelectInput label="Type" source="requestType" choices={REQUEST_TYPE_CHOICES} />
  </Filter>
);

export const BookingRequestList = () => (
  <List
    filters={<BookingRequestFilter />}
    filterDefaultValues={{ view: "open" }}
    sort={{ field: "preferredDate", order: "ASC" }}
    perPage={50}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <FunctionField<BookingRequestRecord>
        label="Preferred Date"
        sortBy="preferredDate"
        render={(record) =>
          record
            ? [formatDate(record.preferredDate), formatTime(record.preferredTime)]
                .filter(Boolean)
                .join(" · ")
            : ""
        }
      />
      <TextField source="requestType" label="Type" />
      <TextField source="name" />
      <FunctionField<BookingRequestRecord>
        label="Email"
        sortBy="email"
        render={(record) =>
          record?.email ? (
            <MuiLink href={`mailto:${record.email}`} onClick={stopRowClick}>
              {record.email}
            </MuiLink>
          ) : null
        }
      />
      <FunctionField<BookingRequestRecord>
        label="Phone"
        render={(record) =>
          record?.phone ? (
            <MuiLink href={`tel:${record.phone}`} onClick={stopRowClick}>
              {record.phone}
            </MuiLink>
          ) : null
        }
      />
      <TextField source="guestCount" label="Guests" />
      <FunctionField label="Status" sortBy="status" render={() => <StatusChip />} />
      <DateField source="createdAt" label="Received" />
      <ShowButton label="" />
    </Datagrid>
  </List>
);

const CommunicationsLog = () => {
  const record = useRecordContext<BookingRequestRecord>();
  if (!record?.communications?.length) return null;
  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Communication History
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Sent</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Subject</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {record.communications.map((entry, index) => (
            <TableRow key={index}>
              <TableCell>
                {entry.sentAt instanceof Date
                  ? format(entry.sentAt, "MMM d, yyyy h:mm a")
                  : ""}
              </TableCell>
              <TableCell>{entry.type}</TableCell>
              <TableCell>{entry.subject}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export const BookingRequestShow = () => (
  <Show>
    <SimpleShowLayout>
      <FunctionField label="Status" render={() => <StatusChip />} />
      <TextField source="requestType" label="Request Type" />
      <FunctionField<BookingRequestRecord>
        label="Preferred Date & Time"
        render={(record) =>
          record
            ? [formatDate(record.preferredDate), formatTime(record.preferredTime)]
                .filter(Boolean)
                .join(" · ")
            : ""
        }
      />
      <FunctionField<BookingRequestRecord>
        label="Scheduled For"
        render={(record) =>
          record?.scheduledDate
            ? [formatDate(record.scheduledDate), formatTime(record.scheduledTime)]
                .filter(Boolean)
                .join(" · ")
            : "Not scheduled yet"
        }
      />
      <TextField source="name" />
      <FunctionField<BookingRequestRecord>
        label="Email"
        render={(record) =>
          record?.email ? (
            <MuiLink href={`mailto:${record.email}`}>{record.email}</MuiLink>
          ) : null
        }
      />
      <FunctionField<BookingRequestRecord>
        label="Phone"
        render={(record) =>
          record?.phone ? (
            <MuiLink href={`tel:${record.phone}`}>{record.phone}</MuiLink>
          ) : null
        }
      />
      <TextField source="guestCount" label="Estimated Guests" />
      <TextField source="message" label="Message" />
      <TextField source="adminNotes" label="Admin Notes" />
      <DateField source="createdAt" label="Received" showTime />
      <BookingRequestActions />
      <CommunicationsLog />
    </SimpleShowLayout>
  </Show>
);

export const BookingRequestEdit = () => (
  <Edit>
    <SimpleForm>
      <TextInput disabled source="id" />
      <SelectInput
        source="status"
        choices={[
          { id: "new", name: "New" },
          { id: "contacted", name: "Contacted" },
          { id: "approved", name: "Approved" },
          { id: "rejected", name: "Rejected" },
        ]}
      />
      <TextInput source="name" />
      <TextInput source="email" />
      <TextInput source="phone" />
      <SelectInput source="requestType" choices={REQUEST_TYPE_CHOICES} />
      <DateInput source="preferredDate" />
      <TextInput source="preferredTime" type="time" />
      <DateInput source="scheduledDate" />
      <TextInput source="scheduledTime" type="time" />
      <TextInput source="guestCount" />
      <TextInput source="message" multiline rows={4} fullWidth />
      <TextInput source="adminNotes" multiline rows={3} fullWidth />
    </SimpleForm>
  </Edit>
);

const BookingRequestsAdmin = {
  list: BookingRequestList,
  show: BookingRequestShow,
  edit: BookingRequestEdit,
};

export default BookingRequestsAdmin;
